import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { BsShieldLock } from 'react-icons/bs';
import { END_POINT } from '../../Redux/AdminReducer/action';
import { Button, Input } from '../../Components/UI';

/**
 * Landing page for the emailed password reset link.
 * The token is validated up front so an expired link says so instead of
 * failing only after the user has typed a new password.
 */
export default function ResetPasswordLink() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('checking'); // checking | valid | invalid | done
  const [account, setAccount] = useState(null);
  const [message, setMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const verifyToken = async () => {
      try {
        const res = await axios.get(`${END_POINT}/resetPassword/public/resetToken/${token}`);
        if (cancelled) return;
        setAccount(res?.data);
        setStatus('valid');
      } catch (error) {
        if (cancelled) return;
        setMessage(error?.response?.data || 'This reset link is invalid or has expired.');
        setStatus('invalid');
      }
    };

    verifyToken();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const validate = () => {
    const errors = {};
    if (!newPassword) {
      errors.newPassword = 'Enter a new password';
    } else if (newPassword.trim().length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      await axios.post(`${END_POINT}/resetPassword/public/resetPassword/${token}`, {
        newPassword: newPassword.trim(),
      });
      setStatus('done');
    } catch (error) {
      toast.error(error?.response?.data || 'Could not reset the password. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md border border-slate-200/80 p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 mb-3">
            <BsShieldLock className="w-5 h-5" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Set a New Password</h1>
          {status === 'valid' && (
            <p className="text-xs text-slate-500 mt-1">
              {account?.username ? `For ${account.username}` : 'Choose a password for your account'}
            </p>
          )}
        </div>

        {status === 'checking' && (
          <p className="text-sm text-slate-500 text-center py-6">Checking your reset link…</p>
        )}

        {status === 'invalid' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl bg-rose-50 border border-rose-200 p-4">
              <FiAlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <p className="text-sm text-rose-800">{message}</p>
            </div>
            <Button variant="primary" size="md" className="w-full" onClick={() => navigate('/login')}>
              Back to Login
            </Button>
          </div>
        )}

        {status === 'done' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              <FiCheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">
                Your password has been updated. You can now log in with it.
              </p>
            </div>
            <Button variant="primary" size="md" className="w-full" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </div>
        )}

        {status === 'valid' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="New Password"
              name="newPassword"
              type="password"
              placeholder="At least 6 characters"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setFormError((prev) => ({ ...prev, newPassword: null }));
              }}
              error={formError.newPassword}
              required
            />
            <Input
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              placeholder="Re-enter the new password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setFormError((prev) => ({ ...prev, confirmPassword: null }));
              }}
              error={formError.confirmPassword}
              required
            />
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="w-full"
              isLoading={isSaving}
            >
              Update Password
            </Button>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full text-sm text-sky-600 hover:text-sky-700 font-medium"
            >
              Back to Login
            </button>
          </form>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
}
